import { Address, Entity } from './common';
import { TenantLanguage } from './tenant';
import { SportType } from './sport';

export interface User extends Entity {
  name: string;
  firstLastName: string;
  secondLastName?: string;
  email: string;
  documentType: string | 'dni' | 'nie' | 'nia';
  document: string;
  documentImage?: string;
  phone: string;
  birthDate: Date;
  address: Address;
  gender: UserGender;
  profile?: ProfileType;
  position?: PositionType;
  racketBrand?: Racket;
  racketModel?: string;
  photo?: string;
  fcmToken?: string;
  //
  roles: UserRole[];
  /**
   * Optional per-sport role assignments for multi-sport club tenants.
   * Allows a user to have different roles in different sports within the same club.
   * e.g. MANAGER in football, PLAYER in basketball.
   * When absent, the main `roles` field applies to all sports.
   */
  sportRoles?: {
    sport: SportType;
    role: UserRole;
    /** Optional: specific teams this user manages/plays in for this sport */
    teamIds?: string[];
  }[];
  clubId?: string;
  licenseNumber?: string;
  stats?: MatchStats;
  //
  history?: {
    teams?: {
      [scope: string]: {
        fromTeamId: string;
        toTeamId: string;
      };
    };
  };
  iban?: string;
  stripeCustomerId?: string;
  /** Preferred UI language for this user (overrides tenant default) */
  uiLanguage?: TenantLanguage;
  /**
   * Membership status within this tenant.
   * - `active`:    full access — default for existing users
   * - `pending`:   registered but awaiting admin approval (can auth, redirected to waiting screen)
   * - `suspended`: access revoked temporarily (can auth, shown suspension notice)
   *
   * Not stored in JWT claims — allows real-time status changes without token refresh.
   * Checked by guards via Firestore doc read on login.
   */
  membershipStatus?: 'active' | 'pending' | 'suspended';
}

export type MatchStats = {
  [scope: string]: {
    [level: number]: number;
  };
};

export enum UserGender {
  MALE = 'MALE',
  FEMALE = 'FEMALE',
}

/** Roles stored as Firebase Auth custom claims. SUPER_ADMIN is platform-level only. */
export enum UserRole {
  SUPER_ADMIN = 'SUPER_ADMIN',
  ADMIN = 'ADMIN',
  MANAGER = 'MANAGER',
  PLAYER = 'PLAYER',
  REFEREE_COORDINATOR = 'REFEREE_COORDINATOR',
}

export enum ProfileType {
  RIGHT = 'RIGHT',
  LEFT = 'LEFT',
}

export enum Racket {
  ARTENGO = 'ARTENGO',
  BABOLAT = 'BABOLAT',
  DUNLOP = 'DUNLOP',
  HEAD = 'HEAD',
  MASTERPRO = 'MASTERPRO',
  PRINCE = 'PRINCE',
  PROKENNEX = 'PROKENNEX',
  TECNIFIBRE = 'TECNIFIBRE',
  URBALL = 'URBALL',
  VOLKL = 'VOLKL',
  WILSON = 'WILSON',
  YONEX = 'YONEX',
  OTHER = 'OTHER',
}

export enum PositionType {
  DELANTERO = 'DELANTERO',
  ZAGUERO = 'ZAGUERO',
  BOTH = 'BOTH',
}

export interface UserRegistrationFindRequest {
  document: string;
  tenantId: string;
}

export interface UserUnsubscribe {
  userId: string;
}

export interface UserSuggested extends Entity {
  document: string;
  name: string;
  firstLastName: string;
  secondLastName: string;
  clubId: string;
  photo: string;
}
